import ordersKeys from 'modules/orders/util/orders-keys';
import React, {useEffect, useMemo, useState} from 'react';
import useApiQuery from 'shared/hooks/use-api-query';

import {Container, Info, TitleBar, Input} from './styles';
import {OrderProps, OrderStatus} from 'modules/orders/types';
import {useNavigate} from 'react-router-dom';
import FilterList from 'shared/migration-components/filter-list';
import OrderStatusList from './components/order-status-list';
import Button from 'shared/migration-components/button';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import useProjection from 'shared/migration-hooks/use-projection';
import {FilterOptionProps} from 'shared/migration-components/filter-list/types';
import io from 'socket.io-client';
import useUpdateOrder from 'modules/orders/util/use-update-orders';
import {useDebounce} from 'use-debounce';
import Loading from 'shared/migration-components/loading';
import Empty from 'shared/migration-components/empty';
import DateFilter, {FilterEnum} from 'shared/components/date-filter';
import {socketUrl} from 'shared/hooks/use-api';
import last3HoursFilter from 'shared/util/last-3-hours-filter';
import filterEnumToDateFilter from 'shared/util/filter-enum-to-date-filter';

interface GroupBy<T> {
  [key: string]: T[];
}

const OrdersByStatus: React.FC = () => {
  const {companyId, cityId, accessControl} = useAccessControls();
  const {filter} = useProjection<OrderProps>('orders', [], {
    defaultFilters: {
      createdAt: JSON.stringify(last3HoursFilter) as any,
    },
    overrideDefaultValues: true,
  });
  const updateOrder = useUpdateOrder();

  const [search, setSearch] = useState<string>();
  const [debouncedSearch] = useDebounce(search, 1000);

  const socket = useMemo(
    () => io(socketUrl, {transports: ['websocket']}),
    [socketUrl],
  );

  useEffect(() => {
    return () => {
      socket.disconnect();
    };
  }, [socket]);

  const socketKey = cityId ?? companyId;

  useEffect(() => {
    if (!socketKey) return;

    const handler = (data: OrderProps) => {
      updateOrder(data, data.currentStatusName);
    };

    socket.on(socketKey, handler);

    return () => {
      socket.off(socketKey, handler);
    };
  }, [socket, socketKey]);

  const options: FilterOptionProps[] = [
    {field: 'number', label: 'Id', type: 'text', valueAsNumber: true},
    {field: 'user.name', label: 'Cliente', type: 'text'},
    {field: 'user.phone', label: 'Telefone', type: 'text'},
    {field: 'address.street', label: 'Rua', type: 'text'},
    {field: 'address.number', label: 'Número', type: 'text'},
    {field: 'address.city', label: 'Cidade', type: 'text'},
    {field: 'address.neighborhood', label: 'Bairro', type: 'text'},
    {field: 'address.state', label: 'Estado', type: 'text'},
    {field: 'deliveryMan.user.name', label: 'Entregador', type: 'text'},
    {field: 'company.name', label: 'Empresa', type: 'text'},
    {field: 'payment.name', label: 'Forma de pagamento', type: 'text'},
    {field: 'updatedAt', label: 'Atualizado em', type: 'date'},
    {field: 'total', label: 'Total', type: 'text', valueAsNumber: true},
    {
      field: 'deliveryPrice',
      label: 'Taxa de entrega',
      type: 'text',
      valueAsNumber: true,
    },
  ];

  const filters = Object.keys(filter.filters).reduce((filters, key) => {
    const currentFilter = filter.filters[key];
    const option = options.find((option) => option.field === key);

    const filterCondition = Array.isArray(
      eval(currentFilter as unknown as string),
    );

    if (!Array.isArray(currentFilter) || typeof option === 'string')
      return {
        ...filters,
        [key]: filterCondition ? currentFilter : JSON.stringify(currentFilter),
      };

    const filterKey = option?.field as string;

    return {
      ...filters,
      [filterKey]: filterCondition
        ? JSON.stringify(currentFilter)
        : currentFilter,
    };
  }, {});

  const excludedStatus = [
    OrderStatus.delivery_man_assigned,
    OrderStatus.delivery_man_auto_assigned,
  ];

  const {data, isLoading} = useApiQuery<GroupBy<OrderProps>>({
    url: '/orders/all',
    queryKey: ordersKeys.all(),
    refetchInterval: 1000 * 60,
    params: {
      ...filters,
      search: debouncedSearch,
      excludedStatus: companyId
        ? [
            OrderStatus.delivery_man_assigned,
            OrderStatus.delivery_man_auto_assigned,
            OrderStatus.awaiting_payment,
          ].join(',')
        : excludedStatus.join(','),
    },
  });

  const navigate = useNavigate();

  const ordersCount = Object.values(data ?? {}).reduce(
    (count, data) => count + data.length,
    0,
  );

  const isEmpty = Object.values(data ?? {}).every((data) => data.length === 0);

  return (
    <Container>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <TitleBar>Pedidos</TitleBar>
        {accessControl?.companyId && (
          <Button title="LANÇAR PEDIDO" onClick={() => navigate('cart')} />
        )}
      </div>
      <DateFilter
        {...{
          onFilterChange: (filterType, dateInterval) => {
            const filterQuery = filterEnumToDateFilter({
              filter: filterType,
              dateInterval,
              startDateField: 'createdAt',
            });
            return filter.onChange(filterQuery);
          },
          defaultValues: last3HoursFilter as any,
          startDateField: 'createdAt',
          filter: FilterEnum.last3hours,
        }}
      />
      <FilterList filterOptions={options} {...filter} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
        }}>
        <Info>
          Mostrando {ordersCount} resultados do total de {ordersCount}{' '}
          ocorrências
        </Info>
        <Input
          {...{
            onChange: (e) => setSearch(e.target.value),
            value: search,
            placeholder: 'Pesquisar',
          }}
        />
      </div>
      {Object.keys(data ?? {})
        .filter((status) => data[status].length)
        .map((status) => (
          <OrderStatusList
            key={status}
            {...{status: status as OrderStatus, data: data[status]}}
          />
        ))}
      {isLoading && <Loading />}
      {!isLoading && isEmpty && <Empty text="Nenhum pedido encontrado" />}
    </Container>
  );
};

export default OrdersByStatus;
